import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { queryKeys } from '@/lib/queryKeys';
import { queueOrExecuteInvalidation } from '@/lib/modalProtection';

/**
 * Hook pour s'abonner aux changements des affectations d'un chantier en temps réel.
 * Migré : ajoute l'invalidation React Query en plus du callback legacy.
 */
export function useAffectationsRealtime(
  chantierId: string | null,
  onChange: () => void
) {
  const queryClient = useQueryClient();
  const { entrepriseId } = useSubscription();

  // Debounce pour éviter les rafales d'invalidations lors de la reconnexion WebSocket
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleChange = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (import.meta.env.DEV) console.log('[useAffectationsRealtime] Changement détecté pour chantier:', chantierId);
      // Si une modal de saisie est ouverte, mettre en file d'attente
      queueOrExecuteInvalidation(() => {
        onChange();
        if (entrepriseId) {
          queryClient.invalidateQueries({
            queryKey: queryKeys.affectations.chantier(entrepriseId, chantierId!),
          });
          queryClient.invalidateQueries({
            queryKey: queryKeys.equipe.stats(entrepriseId),
          });
        }
      });
    }, 500);
  }, [chantierId, onChange, queryClient, entrepriseId]);

  useEffect(() => {
    if (!chantierId) return;

    const channel = supabase
      .channel(`affectations:${chantierId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'affectations_chantiers',
          filter: `chantier_id=eq.${chantierId}`,
        },
        handleChange
      )
      .subscribe();

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      supabase.removeChannel(channel);
    };
  }, [chantierId, handleChange]);
}
